/**
 * Research Assistant Message and Progress Types
 *
 * Shared types for Research Assistant messages and progress tracking
 * used across multiple features.
 */

export enum ResearchAssistantMessageEnumLegacy {
  INFO = 'info',
  SAVE = 'save',
  PROCESS = 'process',
  REVERT = 'revert',
  MODEL_CHANGE = 'model_change',
  CONCEPT_GROUP_REVIEW = 'concept_group_review',
  OMOP_MAPPING_RESULTS = 'omop_mapping_results',
  ANALYTICAL_VARIABLE = 'analytical_variable',
  PROGRESS = 'progress',
}

export enum ProgressStepType {
  UNDERSTANDING_REQUEST = 'understanding_request',
  GENERATING_COHORT_DEFINITION = 'generating_cohort_definition',
  FINDING_RELEVANT_CONCEPT_GROUPS = 'finding_relevant_concept_groups',
  UPDATING_COHORT_DEFINITION = 'updating_cohort_definition',
  SUMMARIZING_UPDATES = 'summarizing_updates',

  // Detailed concept group creation steps
  SETTING_DOMAINS = 'setting_domains',
  GETTING_RELATED_PHRASES = 'getting_related_phrases',
  FINDING_CANDIDATE_CONCEPTS = 'finding_candidate_concepts',
  REMOVING_DUPLICATES = 'removing_duplicates',
  FILTERING_BY_DESCRIPTION = 'filtering_by_description',
  SELECTING_FINAL_CONCEPTS = 'selecting_final_concepts',
  FETCHING_DESCENDANT_CONCEPTS = 'fetching_descendant_concepts',
  COMBINING_CONCEPTS = 'combining_concepts',
  FINALIZING_CONCEPT_GROUP = 'finalizing_concept_group',

  // Refined concept group creation steps
  IDENTIFYING_RELEVANT_OMOP_DOMAINS = 'identifying_relevant_omop_domains',
  FINDING_MATCHING_OMOP_CONCEPTS = 'finding_matching_omop_concepts',
  REFINING_CONCEPT_MATCHES = 'refining_concept_matches',
  EXPANDING_TO_RELATED_CONCEPTS = 'expanding_to_related_concepts',

  // Cohort definition generation sub-steps
  ANALYZING_STUDY_CRITERIA = 'analyzing_study_criteria',
  DEFINING_ANALYTICAL_VARIABLES = 'defining_analytical_variables',
  UPDATING_ANALYTICAL_VARIABLES = 'updating_analytical_variables',
  REMOVING_ANALYTICAL_VARIABLES = 'removing_analytical_variables',
  ASSEMBLING_COHORT_DEFINITION = 'assembling_cohort_definition',

  // OMOP code mapping steps
  OMOP_PROCESSING = 'omop_processing',
}

export const PROGRESS_STEP_MESSAGES: Record<ProgressStepType, string> = {
  [ProgressStepType.UNDERSTANDING_REQUEST]: 'Understanding request',
  [ProgressStepType.GENERATING_COHORT_DEFINITION]: 'Generating cohort definition',
  [ProgressStepType.FINDING_RELEVANT_CONCEPT_GROUPS]: 'Finding relevant concept groups',
  [ProgressStepType.UPDATING_COHORT_DEFINITION]: 'Updating cohort definition',
  [ProgressStepType.SUMMARIZING_UPDATES]: 'Summarizing updates',

  // Detailed concept group creation steps
  [ProgressStepType.SETTING_DOMAINS]: 'Setting medical domains',
  [ProgressStepType.GETTING_RELATED_PHRASES]: 'Getting related medical terms',
  [ProgressStepType.FINDING_CANDIDATE_CONCEPTS]: 'Finding candidate concepts',
  [ProgressStepType.REMOVING_DUPLICATES]: 'Removing duplicate concepts',
  [ProgressStepType.FILTERING_BY_DESCRIPTION]: 'Filtering by description relevance',
  [ProgressStepType.SELECTING_FINAL_CONCEPTS]: 'Selecting final seed concepts',
  [ProgressStepType.FETCHING_DESCENDANT_CONCEPTS]: 'Fetching descendant concepts',
  [ProgressStepType.COMBINING_CONCEPTS]: 'Combining seed and descendant concepts',
  [ProgressStepType.FINALIZING_CONCEPT_GROUP]: 'Finalizing concept group',

  // Refined concept group creation steps
  [ProgressStepType.IDENTIFYING_RELEVANT_OMOP_DOMAINS]: 'Identifying relevant OMOP domains',
  [ProgressStepType.FINDING_MATCHING_OMOP_CONCEPTS]: 'Finding matching OMOP concepts',
  [ProgressStepType.REFINING_CONCEPT_MATCHES]: 'Refining concept matches',
  [ProgressStepType.EXPANDING_TO_RELATED_CONCEPTS]: 'Expanding to related concepts',

  // Cohort definition generation sub-steps
  [ProgressStepType.ANALYZING_STUDY_CRITERIA]: 'Identifying necessary cohort updates',
  [ProgressStepType.DEFINING_ANALYTICAL_VARIABLES]: 'Defining analytical variables',
  [ProgressStepType.UPDATING_ANALYTICAL_VARIABLES]: 'Updating analytical variables',
  [ProgressStepType.REMOVING_ANALYTICAL_VARIABLES]: 'Removing analytical variables',
  [ProgressStepType.ASSEMBLING_COHORT_DEFINITION]: 'Assembling cohort definition',

  // OMOP code mapping steps
  [ProgressStepType.OMOP_PROCESSING]: 'Processing codes',
};

export const getProgressStepMessage = (step: ProgressStepType): string => {
  return PROGRESS_STEP_MESSAGES[step] || 'Processing';
};

export const parseProgressStepType = (step: string): ProgressStepType | null => {
  return Object.values(ProgressStepType).includes(step as ProgressStepType)
    ? (step as ProgressStepType)
    : null;
};

export interface ProgressStepInfo {
  step: ProgressStepType;
  message?: string; // Optional - backend can provide custom message (e.g., "Second try - ...")
}

export type AVProgressStatus =
  | 'pending'
  | 'in_progress'
  | 'searching'
  | 'completed'
  | 'failed'
  | 'retrying';

export type AVProgressAction = 'add' | 'modify' | 'remove' | 'study_period';

export interface AVProgressItem {
  avIndex: number;
  name: string;
  action: AVProgressAction;
  status: AVProgressStatus;
  retryMessage?: string;
}

export interface ProgressState {
  currentStep: ProgressStepInfo | null;
  avProgress: AVProgressItem[] | null;
  workflowId: string | null;
}

// Workflow lifecycle event types — used for progress persistence and timeout sync

export interface WorkflowStartedEvent {
  workflow_id: string;
  timeout_seconds: number;
  timeout_at: string; // ISO timestamp
  user_prompt: string;
}

export interface WorkflowCompletedEvent {
  workflow_id: string;
  status: 'completed' | 'failed' | 'timed_out';
}

export interface WorkflowProgressRestore {
  workflow_id: string;
  status: string;
  timeout_at: string;
  timeout_seconds: number;
  user_prompt?: string;
  current_step: { step: string; message?: string } | null;
  av_progress:
    | {
        av_index: number;
        name: string;
        action: AVProgressAction;
        status: AVProgressStatus;
        retry_message?: string;
      }[]
    | null;
}
